import type { IDatadogLogger } from "@indeed/jobsearch-react-native-bundle/shared/logging/DatadogLoggerTypes";

import { datadogLogs } from "@datadog/browser-logs";
import {
  type DatadogWebConfig,
  createDatadogLogsMethods,
  hasRequiredConfig,
  sanitizeUrl,
  scrubContext,
} from "@indeed/jobsearch-react-native-bundle/shared/logging/datadogWebConfig";

/**
 * Datadog config for the RNVJ **web** bundle.
 *
 * Mirrors `web/profile/src/datadog/datadogConfig.ts`: it initializes the
 * Datadog Browser Logs SDK and registers a web `IDatadogLogger`
 * implementation via `setDatadogLoggerImplementation`. Once registered, the
 * shared `DatadogLogger` (and therefore `useLogErrorOnce`, the global error
 * handler, etc.) emit log events to Datadog on web instead of no-op'ing —
 * e.g. the "Apply button missing" event in `OGWrappedIndeedApplyButton`,
 * which previously only fired in native apps (RN-5721).
 *
 * The stateless helpers (config guard, URL/PII scrubbing, and the logs-only
 * logger methods) are shared with Profile via
 * `shared/logging/datadogWebConfig`; only the pieces that diverge by design
 * live here.
 *
 * RUM (`startView` / `addRumAction` / `addRumError`) is intentionally
 * *stubbed* on web: the RN-native `IDatadogLogger` interface exposes those
 * methods, but the RNVJ web bundle only ships the Browser Logs SDK. The RUM
 * methods on the registered logger resolve to no-op so the shared call sites
 * stay platform-agnostic without pulling in `@datadog/browser-rum`.
 *
 * **Why dependency-injected instead of self-initializing (like Profile)?**
 * The RNVJ web bundle ships two variants — non-strict (`shared/`) and strict
 * (`shared-strict/`) — each with its own copy of `DatadogLogger.web`, and
 * `setDatadogLoggerImplementation` mutates module-local state. Registration
 * must therefore hit the *same* `DatadogLogger.web` instance the running app
 * code imports. `scripts/prepare-viewjob-strict.sh` rewrites only the
 * `@indeed/jobsearch-react-native-bundle/<dir>/` package paths inside the
 * generated `index.*.strict.*` entry files, so the entries import the correct
 * variant's `setDatadogLoggerImplementation`/`enrichContext` and pass them in.
 * This module imports neither directly, keeping it variant-agnostic.
 *
 * The host page must inject `globalThis.rnViewjobDatadogConfig` before this
 * runs (see Profile's `ProfileController` + `page.ejs` for the JSF-side
 * injection convention). Without it, initialization is a no-op.
 */

type ViewjobDatadogGlobal = typeof globalThis & {
  rnViewjobDatadogConfig?: Partial<DatadogWebConfig>;
  _tareData?: {
    accountId?: string | null;
  };
  __viewjobDatadogLogsInitialized?: boolean;
  __viewjobDatadogAccountId?: string;
};

/**
 * Bindings supplied by the entry file. They are imported there (not here) via
 * the `@indeed/jobsearch-react-native-bundle/shared/...` package paths so the
 * strict-entry codegen can swap them to `shared-strict/...` for the strict
 * bundle. See the module doc comment for the full rationale.
 */
export type ViewjobDatadogDeps = {
  setDatadogLoggerImplementation: (logger: IDatadogLogger) => void;
  enrichContext: (context?: Record<string, unknown>) => Record<string, unknown>;
};

const ACCOUNT_ID = "accountId";

function getGlobal(): ViewjobDatadogGlobal {
  return globalThis as ViewjobDatadogGlobal;
}

function syncAccountIdContext(viewjobGlobal = getGlobal()): void {
  const accountId = viewjobGlobal._tareData?.accountId;
  if (accountId == null || accountId === "") {
    // Host cleared the account (e.g. logout/reset). Drop the stale tag so
    // later logs aren't attributed to the previous account.
    if (viewjobGlobal.__viewjobDatadogAccountId !== undefined) {
      delete viewjobGlobal.__viewjobDatadogAccountId;
      datadogLogs.removeGlobalContextProperty(ACCOUNT_ID);
    }
    return;
  }

  const normalizedAccountId = String(accountId);
  if (viewjobGlobal.__viewjobDatadogAccountId === normalizedAccountId) {
    return;
  }

  viewjobGlobal.__viewjobDatadogAccountId = normalizedAccountId;
  datadogLogs.setGlobalContextProperty(ACCOUNT_ID, normalizedAccountId);
}

function scrubEvent(event: {
  view?: { url?: string; referrer?: string };
  resource?: { url?: string };
  context?: unknown;
}): boolean {
  // Keep host context fresh in case _tareData hydrates after initial Datadog init.
  syncAccountIdContext();

  if (event.view) {
    event.view.url = sanitizeUrl(event.view.url);
    event.view.referrer = sanitizeUrl(event.view.referrer);
  }

  if (event.resource) {
    event.resource.url = sanitizeUrl(event.resource.url);
  }

  if (event.context) {
    event.context = scrubContext(event.context);
  }

  return true;
}

function createViewjobWebDatadogLogger(deps: ViewjobDatadogDeps): IDatadogLogger {
  return {
    ...createDatadogLogsMethods(datadogLogs.logger, deps.enrichContext),

    // RUM methods are stubbed on web: the RNVJ web bundle only ships the
    // Browser Logs SDK (no `@datadog/browser-rum`). These no-ops keep the
    // shared `IDatadogLogger` interface satisfied for cross-platform call
    // sites (e.g. screen-view tracking, addRumError from
    // globalErrorHandler, addFeatureFlagEvaluation from proctor bucket
    // reporting) while emitting nothing on web.
    startView: (): Promise<void> => Promise.resolve(),
    stopView: (): Promise<void> => Promise.resolve(),
    addRumAction: (): Promise<void> => Promise.resolve(),
    addFeatureFlagEvaluation: (): Promise<void> => Promise.resolve(),
    addRumError: (): Promise<void> => Promise.resolve(),
  };
}

export function initializeViewjobDatadog(deps: ViewjobDatadogDeps): void {
  const viewjobGlobal = getGlobal();

  const config = viewjobGlobal.rnViewjobDatadogConfig;
  if (!hasRequiredConfig(config)) {
    console.info(
      "[viewjob-datadog] init skipped: globalThis.rnViewjobDatadogConfig missing or incomplete",
      config,
    );
    return;
  }

  try {
    // The Datadog SDK is a page-level singleton, so its init is guarded to
    // run only once. Logger registration, however, must run on *every*
    // call: the strict and non-strict bundles each own a distinct
    // module-local `DatadogLogger.web`, so a shared "already initialized"
    // guard would leave every entry after the first no-op'ing.
    if (!viewjobGlobal.__viewjobDatadogLogsInitialized) {
      datadogLogs.init({
        clientToken: config.clientToken,
        site: config.site,
        service: config.service,
        version: config.version,
        env: config.env,
        sessionSampleRate: config.sessionSampleRate,
        forwardErrorsToLogs: true,
        beforeSend: scrubEvent,
      });
      viewjobGlobal.__viewjobDatadogLogsInitialized = true;
      console.info("[viewjob-datadog] SDK initialized", {
        service: config.service,
        env: config.env,
        version: config.version,
        site: config.site,
        sessionSampleRate: config.sessionSampleRate,
      });
    }

    deps.setDatadogLoggerImplementation(createViewjobWebDatadogLogger(deps));
    syncAccountIdContext(viewjobGlobal);
    console.info("[viewjob-datadog] logger implementation registered");
  } catch (error) {
    // Never let a Datadog failure block the app. `__viewjobDatadogLogsInitialized`
    // stays unset if `init` threw, so a later call can retry the SDK init.
    console.error("[viewjob-datadog] initialization failed", error);
  }
}
